// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the tidy Assistant chat's chrome (design/assistant-dm-cleanup), the pure parts: what the
// list drawer groups, what "Tidy up" archives, how a search hit is classified and grouped, which
// items a card's ⋯ menu offers and how History groups its rows. No React, no Lingui: the components
// in this folder turn these into words.

import type {
	AssistantChatInfo,
	AssistantChatLayout,
	AssistantChatMessageLike,
	AssistantChatRole,
} from '@app/features/konvo_assistant/utils/AssistantChatLayout';
import {
	ASSISTANT_CHAT_MUTABLE_KINDS,
	type AssistantChatHistoryEvent,
	type AssistantChatKind,
	type AssistantChatState,
	assistantChatKindOfEmbed,
} from '@konvo/schema/src/domains/konvo/AssistantChat';

const DAY_MS = 86_400_000;
/** The archive endpoint's limits (AssistantChatArchiveRequest). */
const MAX_ARCHIVE_MESSAGES = 500;
const MAX_ARCHIVE_CONVERSATIONS = 100;

export function startOfLocalDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBefore(day: Date, count: number): Date {
	return new Date(day.getFullYear(), day.getMonth(), day.getDate() - count);
}

export type AssistantDayBucket = 'today' | 'yesterday' | 'week' | 'earlier';

/** Today / Yesterday / This week (the six days before yesterday) / Earlier, in local time. */
export function dayBucket(at: Date, now: Date): AssistantDayBucket {
	const today = startOfLocalDay(now);
	if (at >= today) return 'today';
	if (at >= daysBefore(today, 1)) return 'yesterday';
	if (at >= daysBefore(today, 7)) return 'week';
	return 'earlier';
}

export interface AssistantDrawerConversation {
	id: string;
	title: string;
	updatedAt: Date;
	/** Loaded messages, oldest first (empty when none of it is loaded yet). */
	messageIds: Array<string>;
	/** The first message the server knows of, to jump to when nothing is loaded. */
	firstMessageId: string | null;
	guessed: boolean;
}

export interface AssistantDrawerGroup {
	bucket: AssistantDayBucket;
	conversations: Array<AssistantDrawerConversation>;
}

/** The drawer's conversation list: not archived, newest first, grouped by day. */
export function drawerGroups(
	layout: Pick<AssistantChatLayout, 'conversations'>,
	state: AssistantChatState | null,
	now: Date,
): Array<AssistantDrawerGroup> {
	const firstIds = new Map((state?.conversations ?? []).map((c) => [c.id, c.message_ids[0] ?? null]));
	const groups: Array<AssistantDrawerGroup> = [];
	for (const conversation of layout.conversations) {
		if (conversation.archived) continue;
		const bucket = dayBucket(conversation.updatedAt, now);
		let group = groups.find((candidate) => candidate.bucket === bucket);
		if (!group) {
			group = {bucket, conversations: []};
			groups.push(group);
		}
		group.conversations.push({
			id: conversation.id,
			title: conversation.title,
			updatedAt: conversation.updatedAt,
			messageIds: conversation.messageIds,
			firstMessageId: firstIds.get(conversation.id) ?? conversation.messageIds[0] ?? null,
			guessed: conversation.guessed,
		});
	}
	const order: Array<AssistantDayBucket> = ['today', 'yesterday', 'week', 'earlier'];
	return groups.sort((a, b) => order.indexOf(a.bucket) - order.indexOf(b.bucket));
}

/** What waits on the person, by sort, for the "Needs you" line (e.g. "Agent question, 2 drafts"). */
export interface AssistantNeedsSummary {
	total: number;
	agentQuestions: number;
	drafts: number;
	other: number;
}

export function needsYouSummary(layout: Pick<AssistantChatLayout, 'needsYouIds' | 'infos'>): AssistantNeedsSummary {
	const summary: AssistantNeedsSummary = {total: 0, agentQuestions: 0, drafts: 0, other: 0};
	for (const id of layout.needsYouIds) {
		const kind = layout.infos.get(id)?.kind ?? null;
		summary.total++;
		if (kind === 'agents') summary.agentQuestions++;
		else if (kind === 'action') summary.drafts++;
		else summary.other++;
	}
	return summary;
}

export interface AssistantTidyUpSelection {
	messageIds: Array<string>;
	conversationIds: Array<string>;
	/** Finished cards it archives. */
	cards: number;
	/** Past conversations it archives. */
	conversations: number;
	total: number;
}

/**
 * "Tidy up": every finished update (sent, done, expired, past reminders) that is not pinned, and
 * every past conversation from before yesterday. Nothing that still waits on the person, and never
 * the current conversation, nor a conversation not fully loaded (its waiting cards can't be checked). Guessed conversations (older chats the server has not indexed) go as
 * their loaded message ids.
 */
export function selectTidyUp(
	layout: Pick<AssistantChatLayout, 'infos' | 'conversations' | 'currentConversationId'>,
	now: Date,
): AssistantTidyUpSelection {
	const messageIds: Array<string> = [];
	let cards = 0;
	for (const info of layout.infos.values()) {
		if (info.role !== 'update' || !info.settled || info.archived || info.pinned || info.needsYou) continue;
		messageIds.push(info.id);
		cards++;
	}
	const conversationIds: Array<string> = [];
	let conversations = 0;
	const yesterday = daysBefore(startOfLocalDay(now), 1);
	for (const conversation of layout.conversations) {
		if (conversation.archived || conversation.id === layout.currentConversationId) continue;
		if (conversation.updatedAt >= yesterday) continue;
		// Only what is loaded can be checked for waiting cards: skip conversations not fully loaded.
		if (!conversation.guessed && conversation.messageIds.length < conversation.count) continue;
		if (conversation.messageIds.some((id) => layout.infos.get(id)?.needsYou)) continue;
		if (conversation.guessed) {
			if (conversation.messageIds.length === 0) continue;
			messageIds.push(...conversation.messageIds);
		} else {
			conversationIds.push(conversation.id);
		}
		conversations++;
	}
	const cappedMessages = messageIds.slice(0, MAX_ARCHIVE_MESSAGES);
	const cappedConversations = conversationIds.slice(0, MAX_ARCHIVE_CONVERSATIONS);
	return {
		messageIds: cappedMessages,
		conversationIds: cappedConversations,
		cards,
		conversations,
		total: cards + conversations,
	};
}

export type AssistantSearchCategory = 'chats' | 'updates' | 'drafts';

export interface AssistantSearchHitClass {
	category: AssistantSearchCategory;
	kind: AssistantChatKind | null;
	role: AssistantChatRole;
	archived: boolean;
	/** The conversation a chat hit belongs to, when known. */
	conversationId: string | null;
}

function kindOfMessage(message: AssistantChatMessageLike): AssistantChatKind | null {
	for (const embed of message.embeds) {
		const kind = assistantChatKindOfEmbed(embed);
		if (kind) return kind;
	}
	return null;
}

function hasDoneField(message: AssistantChatMessageLike): boolean {
	return message.embeds.some((embed) => (embed.fields ?? []).some((field) => field.name === 'done'));
}

/**
 * Sorts a search hit into Chats (the person's messages and Konvo's replies), Updates (cards Konvo
 * posted by itself) or Drafts (action previews not done yet). Uses the layout's info when the
 * message is loaded, else reads the message itself and the server's conversation index.
 */
export function classifySearchHit(
	message: AssistantChatMessageLike,
	info: AssistantChatInfo | undefined,
	selfId: string | null,
	state: AssistantChatState | null,
): AssistantSearchHitClass {
	const indexed = state?.conversations.find((conversation) => conversation.message_ids.includes(message.id));
	const archived =
		info?.archived ?? (Boolean(state?.archived_message_ids.includes(message.id)) || Boolean(indexed?.archived));
	const kind = info ? info.kind : kindOfMessage(message);
	let role: AssistantChatRole;
	if (info) role = info.role;
	else if (message.author.id === selfId) role = 'you';
	else if (indexed || kind === null) role = 'konvo';
	else role = 'update';
	const conversationId = info?.conversationId ?? indexed?.id ?? null;
	const draft = kind === 'action' && (info ? info.state !== 'Done' : !hasDoneField(message));
	const category: AssistantSearchCategory = draft ? 'drafts' : role === 'update' ? 'updates' : 'chats';
	return {category, kind, role, archived, conversationId};
}

export type AssistantSearchFilterValue = 'everything' | AssistantSearchCategory;

export function matchesSearchFilter(
	hit: Pick<AssistantSearchHitClass, 'category' | 'archived'>,
	filter: AssistantSearchFilterValue,
	includeArchived: boolean,
): boolean {
	if (hit.archived && !includeArchived) return false;
	return filter === 'everything' || hit.category === filter;
}

/** Search groups: Today, then Earlier (newest first inside each). */
export function searchDayGroups<T extends {at: Date}>(
	items: ReadonlyArray<T>,
	now: Date,
): Array<{key: 'today' | 'earlier'; items: Array<T>}> {
	const today = startOfLocalDay(now);
	const sorted = [...items].sort((a, b) => b.at.getTime() - a.at.getTime());
	const groups: Array<{key: 'today' | 'earlier'; items: Array<T>}> = [];
	const todayItems = sorted.filter((item) => item.at >= today);
	const earlierItems = sorted.filter((item) => item.at < today);
	if (todayItems.length) groups.push({key: 'today', items: todayItems});
	if (earlierItems.length) groups.push({key: 'earlier', items: earlierItems});
	return groups;
}

export interface HighlightPart {
	text: string;
	match: boolean;
}

function escapeRegExp(text: string): string {
	return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function searchTerms(query: string): Array<string> {
	const words = query
		.trim()
		.split(/\s+/)
		.map((word) => word.replace(/^["'“”‘’]+|["'“”‘’?!.,:;]+$/g, ''))
		.filter((word) => word.length >= 2);
	return [...new Set(words.map((word) => word.toLowerCase()))].sort((a, b) => b.length - a.length);
}

/** Splits text into plain and matching parts for the query's words (case-insensitive). */
export function highlightParts(text: string, query: string): Array<HighlightPart> {
	const terms = searchTerms(query);
	if (!text || terms.length === 0) return [{text, match: false}];
	const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
	const parts: Array<HighlightPart> = [];
	let last = 0;
	for (const match of text.matchAll(pattern)) {
		const index = match.index ?? 0;
		if (index > last) parts.push({text: text.slice(last, index), match: false});
		parts.push({text: match[0], match: true});
		last = index + match[0].length;
	}
	if (last < text.length) parts.push({text: text.slice(last), match: false});
	return parts;
}

/** Strips Markdown and mentions so a snippet reads as plain text. */
export function plainText(text: string): string {
	return text
		.replace(/<[@#:!][^>]*>/g, '')
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
		.replace(/[*_~`>#]+/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

/** A window of the text around the first match, with ellipses where it was cut. */
export function snippetAround(text: string, query: string, max = 160): string {
	const plain = plainText(text);
	if (plain.length <= max) return plain;
	const terms = searchTerms(query);
	const lower = plain.toLowerCase();
	const index = terms.reduce((best, term) => {
		const at = lower.indexOf(term);
		return at >= 0 && (best < 0 || at < best) ? at : best;
	}, -1);
	if (index < 0) return `${plain.slice(0, max - 1).trimEnd()}…`;
	const start = Math.max(0, index - Math.floor(max / 3));
	const end = Math.min(plain.length, start + max);
	return `${start > 0 ? '…' : ''}${plain.slice(start, end).trim()}${end < plain.length ? '…' : ''}`;
}

/** Card fields that carry encoded data or controls rather than text (KonvoCard readFields). */
const STRUCTURAL_FIELD = /^(response:|nudge:|card$|done$|button|action|input:)/;

/** The text a hit is found by: the message text, else its card's description and text fields. */
export function messageSearchText(message: AssistantChatMessageLike): string {
	if (message.content.trim()) return message.content;
	const embed = message.embeds[0];
	if (!embed) return '';
	return [
		embed.description,
		...(embed.fields ?? []).filter((field) => !STRUCTURAL_FIELD.test(field.name)).map((field) => field.value),
	]
		.filter(Boolean)
		.join(' · ');
}

export interface AssistantCardMenuSpec {
	kind: AssistantChatKind | null;
	pinned: boolean;
	/** Null when this kind cannot be muted (replies, drafts, dashboards). */
	mute: {kind: AssistantChatKind; muted: boolean} | null;
	archived: boolean;
}

/** What a card's ⋯ menu offers: History, Pin or Unpin, Mute or Unmute its kind, Archive or Unarchive. */
export function cardMenuSpec(
	kind: AssistantChatKind | null,
	flags: {pinned: boolean; archived: boolean; muted: boolean},
): AssistantCardMenuSpec {
	const mutable = kind !== null && ASSISTANT_CHAT_MUTABLE_KINDS.includes(kind);
	return {
		kind,
		pinned: flags.pinned,
		mute: mutable && kind ? {kind, muted: flags.muted} : null,
		archived: flags.archived,
	};
}

export type AssistantHistoryTone = AssistantChatHistoryEvent['tone'];

export interface AssistantHistoryRow {
	key: string;
	at: Date;
	title: string;
	detail: string | null;
	tone: AssistantHistoryTone;
	/** A loaded message to jump to (cards of kinds without a server history). */
	messageId: string | null;
	machineId: string | null;
	threadId: string | null;
}

export function historyRowsFromEvents(events: ReadonlyArray<AssistantChatHistoryEvent>): Array<AssistantHistoryRow> {
	return events
		.map((event, index) => ({
			key: `${event.at}-${index}`,
			at: new Date(event.at),
			title: event.title,
			detail: event.detail ?? null,
			tone: event.tone,
			messageId: null,
			machineId: event.machine_id ?? null,
			threadId: event.thread_id ?? null,
		}))
		.sort((a, b) => b.at.getTime() - a.at.getTime());
}

/** History for kinds the server keeps none of: the loaded cards of that kind, newest first. */
export function historyRowsFromLayout(
	layout: Pick<AssistantChatLayout, 'infos'>,
	kind: AssistantChatKind | null,
): Array<AssistantHistoryRow> {
	const rows: Array<AssistantHistoryRow> = [];
	for (const info of layout.infos.values()) {
		if (info.kind !== kind || info.role === 'you') continue;
		rows.push({
			key: info.id,
			at: info.at,
			title: info.title,
			detail: info.state ?? info.detail,
			tone: info.needsYou ? 'needs' : info.state === 'Done' ? 'done' : 'info',
			messageId: info.id,
			machineId: null,
			threadId: null,
		});
	}
	return rows.sort((a, b) => b.at.getTime() - a.at.getTime());
}

export interface AssistantHistoryDay {
	/** 'today', 'yesterday' or the local day's start time as ms. */
	key: string;
	day: Date;
	rows: Array<AssistantHistoryRow>;
}

export function historyDays(rows: ReadonlyArray<AssistantHistoryRow>, now: Date): Array<AssistantHistoryDay> {
	const today = startOfLocalDay(now);
	const yesterday = daysBefore(today, 1);
	const days: Array<AssistantHistoryDay> = [];
	for (const row of rows) {
		const day = startOfLocalDay(row.at);
		const key =
			day.getTime() === today.getTime()
				? 'today'
				: day.getTime() === yesterday.getTime()
					? 'yesterday'
					: String(day.getTime());
		let group = days.find((candidate) => candidate.key === key);
		if (!group) {
			group = {key, day, rows: []};
			days.push(group);
		}
		group.rows.push(row);
	}
	return days;
}

export function countToday(rows: ReadonlyArray<{at: Date}>, now: Date): number {
	const today = startOfLocalDay(now);
	return rows.filter((row) => row.at >= today).length;
}

/** The message carrying the live Agents card: the server's pick when loaded, else the newest one. */
export function liveAgentsMessageId(
	messages: ReadonlyArray<AssistantChatMessageLike>,
	state: AssistantChatState | null,
): string | null {
	const pick = state?.live.agents;
	if (pick && messages.some((message) => message.id === pick)) return pick;
	for (let index = messages.length - 1; index >= 0; index--) {
		if (kindOfMessage(messages[index]) === 'agents') return messages[index].id;
	}
	return pick ?? null;
}

export function isWithinDays(at: Date, now: Date, days: number): boolean {
	return now.getTime() - at.getTime() < days * DAY_MS;
}
